import i18n from 'i18n-js';
import { addDays, format, isValid, parseISO } from 'date-fns';
import { de, enGB } from 'date-fns/locale';

import {
    ReservationDTO,
    ReservationListFilter,
    ReservationChannel,
    TableAvailabilityDTO,
    UpdateReservationModel,
} from 'api/controllers/reservations';
import { DayKey, HourData } from 'components/restaurantInformation/components/types';
import { Positions } from 'constants/enums';
import { LocalStorageKeys } from 'constants/localStorageKeys';
import { ReservationModule } from 'store/restaurantDetailsStore/reducer';
import { getTodayWorkingHours, isFullDayTimeRange } from './orderInAdvanceHelper';
import { regexHelper } from './regexHelper';
import * as yup from 'yup';

export const RESERVATION_POSITIONS = [Positions.MANAGER, Positions.WAITER];
export const SLOT_MINUTES = 15;
export const MAX_GUESTS_CHIP = 8;
export const DURATION_PRESETS = [60, 90, 120, 180];
export const DATE_FORMAT = 'yyyy-MM-dd';
export const WIRE_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";
type ReservationWallClock = string | Date;

export function getCurrentReservationDay(): string {
    return format(new Date(), DATE_FORMAT);
}

const MINUTES_IN_DAY = 24 * 60;

export const MAX_GUESTS = 1000;
export const MAX_DURATION_MINUTES = 1440;
export const RESERVATION_MAX_LENGTH = {
    firstName: 100,
    lastName: 100,
    phoneNumber: 50,
    email: 256,
    otherAllergies: 500,
    specialRequests: 1000,
};

const MAX_LENGTH_LABELS: Record<keyof typeof RESERVATION_MAX_LENGTH, string> = {
    firstName: 'reservations.fields.firstName',
    lastName: 'reservations.fields.lastName',
    phoneNumber: 'reservations.fields.phone',
    email: 'reservations.fields.email',
    otherAllergies: 'reservations.other',
    specialRequests: 'reservations.fields.specialRequests',
};

const FALLBACK_DURATION_MINUTES = 120;

export interface ReservationFormValues {
    internalNote?: string;
    date: string;
    time: string;
    guestsCount: number | null;
    isMoreThanMaxGuests: boolean;
    durationMinutes: number | null;
    isCustomDuration: boolean;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    allergenIds: string[];
    hasOtherAllergies: boolean;
    otherAllergies: string;
    specialRequests: string;
    tableIds: string[];
    channel: ReservationChannel | null;
}

export type ReservationFormErrors = Partial<
    Record<
        | 'date'
        | 'time'
        | 'guestsCount'
        | 'durationMinutes'
        | 'firstName'
        | 'lastName'
        | 'phoneNumber'
        | 'email'
        | 'otherAllergies'
        | 'specialRequests'
        | 'internalNote'
        | 'channel',
        string
    >
>;

export function getRestaurantWallClock(
    timeZone: string | null | undefined,
    now = new Date()
): Date {
    const [date, time] = getRestaurantWallClockString(timeZone, now).split('T');
    const [year, month, day] = date.split('-').map(Number);
    const [hour, minute, second] = time.split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute, second);
}

export function getRestaurantWallClockString(
    timeZone: string | null | undefined,
    now = new Date()
): string {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: timeZone || 'Etc/UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(now);
    const part = (type: string) => parts.find((item) => item.type === type)!.value;
    return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part(
        'minute'
    )}:${part('second')}`;
}

/** Both values are restaurant wall-clock strings (yyyy-MM-ddTHH:mm:ss), so they compare lexicographically. */
export function isRequestOverdue(
    request: Pick<ReservationDTO, 'reservationStartDateTime'>,
    restaurantWallClock: string
): boolean {
    return request.reservationStartDateTime.slice(0, 19) < restaurantWallClock;
}

function getWallClockString(now: ReservationWallClock): string {
    return typeof now === 'string' ? now : format(now, WIRE_DATE_TIME_FORMAT);
}

export function validateEditReservationForm(
    values: ReservationFormValues,
    now: ReservationWallClock
): ReservationFormErrors {
    const errors = validateReservationForm(values, now);
    const schema = yup.object({
        date: yup
            .string()
            .matches(/^\d{4}-\d{2}-\d{2}$/, i18n.t('reservations.errors.dateInPast'))
            .test(
                'date',
                i18n.t('reservations.errors.dateInPast'),
                (date) => !!date && !Number.isNaN(parseISO(date).getTime())
            ),
        time: yup
            .string()
            .matches(/^([01]\d|2[0-3]):[0-5]\d$/, i18n.t('reservations.errors.timeInPast')),
        channel: yup
            .string()
            .nullable()
            .notOneOf(
                ['Staff'],
                i18n.t('form.errors.required', {
                    name: i18n.t('reservations.fields.channel'),
                })
            )
            .required(
                i18n.t('form.errors.required', {
                    name: i18n.t('reservations.fields.channel'),
                })
            ),
        phoneNumber: yup
            .string()
            .test(
                'phone',
                i18n.t('reservations.invalidPhone'),
                (phone) =>
                    !phone?.trim() ||
                    (/^\+?[\d\s()./-]+$/.test(phone.trim()) &&
                        digitsOnly(phone).length >= 3 &&
                        digitsOnly(phone).length <= 15)
            ),
        internalNote: yup.string().max(
            1000,
            i18n.t('form.errors.maxLength', {
                name: i18n.t('reservations.internalNote'),
                length: 1000,
            })
        ),
    });
    try {
        schema.validateSync(values, { abortEarly: false });
    } catch (error) {
        if (!(error instanceof yup.ValidationError)) throw error;
        error.inner.forEach((issue) => {
            if (
                issue.path === 'channel' ||
                issue.path === 'phoneNumber' ||
                issue.path === 'date' ||
                issue.path === 'time' ||
                issue.path === 'internalNote'
            ) {
                errors[issue.path] = issue.message;
            }
        });
    }
    return errors;
}

export function validateSeatedReservationForm(
    values: ReservationFormValues,
    now: ReservationWallClock,
    original: ReservationFormValues
): ReservationFormErrors {
    const errors = validateEditReservationForm(values, `${values.date}T00:00:00`);
    const start = toWireDateTime(values.date, values.time);
    const startUnchanged = values.date === original.date && values.time === original.time;
    if (!errors.date && !startUnchanged) {
        if (values.date < getWallClockString(now).slice(0, 10)) {
            errors.date = i18n.t('reservations.errors.dateInPast');
        } else if (!errors.time && start < toWireDateTime(original.date, original.time)) {
            errors.time = i18n.t('reservations.seatedStartTooEarly');
        }
    }
    const intervalChanged = !startUnchanged || values.durationMinutes !== original.durationMinutes;
    if (intervalChanged && !errors.date && !errors.time && !errors.durationMinutes) {
        const end = new Date(
            new Date(`${start}Z`).getTime() + (values.durationMinutes ?? 0) * 60000
        )
            .toISOString()
            .slice(0, 19);
        if (end <= getWallClockString(now)) {
            errors.durationMinutes = i18n.t('reservations.seatedEndInPast');
        }
    }
    return errors;
}

export function getDefaultDurationMinutes(
    settings: Pick<ReservationModule, 'maxReservationDurationMinutes'>
): number | null {
    return settings.maxReservationDurationMinutes > 0
        ? settings.maxReservationDurationMinutes
        : null;
}

export function getMaxGuestsOnline(settings: Pick<ReservationModule, 'maxGuestsOnline'>): number {
    return settings.maxGuestsOnline && settings.maxGuestsOnline > 0
        ? settings.maxGuestsOnline
        : MAX_GUESTS_CHIP;
}

export function minutesToTime(minutes: number): string {
    const wrapped = ((minutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
    const hours = Math.floor(wrapped / 60);
    return `${String(hours).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}

export function timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
}

export function getTimeSlots(): string[] {
    return Array.from({ length: MINUTES_IN_DAY / SLOT_MINUTES }, (_, index) =>
        minutesToTime(index * SLOT_MINUTES)
    );
}

export function toWireDateTime(date: string, time: string): string {
    return `${date}T${time}:00`;
}

export function getDatePart(dateTime: string): string {
    return dateTime.slice(0, 10);
}

export function readPinnedDay(restaurantId: string): string | null {
    const key = LocalStorageKeys.reservationsPinnedDay(restaurantId);
    try {
        const day = localStorage.getItem(key);
        if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day) || !isValid(parseISO(day))) {
            return null;
        }
        if (day < getCurrentReservationDay()) {
            localStorage.removeItem(key);
            return null;
        }
        return day;
    } catch {
        return null;
    }
}

export function writePinnedDay(restaurantId: string, day: string | null): void {
    const key = LocalStorageKeys.reservationsPinnedDay(restaurantId);
    try {
        if (day) {
            localStorage.setItem(key, day);
        } else {
            localStorage.removeItem(key);
        }
    } catch {
        // storage blocked: nothing to persist
    }
}

export const RESERVATION_SEARCH_MIN_LENGTH = 2;
const RESERVATION_SEARCH_MAX_LENGTH = 100;
const PHONE_LIKE_SEARCH = /^[\d\s+\-/().]+$/;

/** Mirrors ReservationSearchHelper.Normalize on the backend: '' when the search must not filter. */
export function normalizeReservationSearch(search: string): string {
    const value = search.trim();
    return value.length < RESERVATION_SEARCH_MIN_LENGTH
        ? ''
        : value.slice(0, RESERVATION_SEARCH_MAX_LENGTH);
}

/** Mirrors the backend search: case-insensitive "contains"; the phone ignores separators and leading zeros. */
export function matchesReservationSearch(
    reservation: Pick<ReservationDTO, 'firstName' | 'lastName' | 'email' | 'phone'>,
    search: string
): boolean {
    const normalized = normalizeReservationSearch(search);
    if (!normalized) {
        return true;
    }
    const text = normalized.toLowerCase();
    const values = [
        reservation.firstName,
        reservation.lastName,
        reservation.email,
        reservation.phone,
    ];
    if (values.some((value) => (value ?? '').toLowerCase().includes(text))) {
        return true;
    }
    const phoneDigits = PHONE_LIKE_SEARCH.test(normalized)
        ? digitsOnly(normalized).replace(/^0+/, '')
        : '';
    return !!phoneDigits && digitsOnly(reservation.phone).includes(phoneDigits);
}

export function matchesReservationFilter(
    reservation: ReservationDTO,
    filter: ReservationListFilter
): boolean {
    return (
        (!filter.day || getDatePart(reservation.reservationStartDateTime) === filter.day) &&
        matchesReservationSearch(reservation, filter.search)
    );
}

export function isReservationFilterActive(filter: ReservationListFilter): boolean {
    return !!filter.day || !!normalizeReservationSearch(filter.search);
}

export function getReservationFilterKey(filter: ReservationListFilter): string {
    return `${filter.day ?? ''}|${normalizeReservationSearch(filter.search)}`;
}

export function getTimePart(dateTime: string): string {
    return dateTime.slice(11, 16);
}

export function canCancelReservation(
    reservation: Pick<ReservationDTO, 'status' | 'isArchived' | 'reservationStartDateTime'>,
    timeZone: string,
    now = new Date()
): boolean {
    const today = getRestaurantWallClockString(timeZone, now).slice(0, 10);
    return (
        reservation.status === 'Confirmed' &&
        !reservation.isArchived &&
        reservation.reservationStartDateTime.slice(0, 10) >= today
    );
}

export function getNoShowAvailability(start: string, timeZone: string, now = new Date()) {
    // Reservation timestamps are restaurant wall-clock values, not browser-local instants.
    const availableFrom = new Date(Date.parse(`${start.slice(0, 19)}Z`) + 15 * 60 * 1000);
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: timeZone || 'Etc/UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(now);
    const part = (type: string) => Number(parts.find((item) => item.type === type)!.value);
    const restaurantNow = Date.UTC(
        part('year'),
        part('month') - 1,
        part('day'),
        part('hour'),
        part('minute'),
        part('second')
    );
    return {
        isAvailable: restaurantNow >= availableFrom.getTime(),
        availableFrom: availableFrom.toISOString().slice(11, 16),
    };
}

export function getUnavailableTables(
    availability: TableAvailabilityDTO[]
): Record<string, string | null> {
    const unavailable: Record<string, string | null> = {};
    availability
        .filter((table) => table.isBooked)
        .forEach((table) => {
            unavailable[table.tableId] = table.bookedFrom ? getTimePart(table.bookedFrom) : null;
        });
    return unavailable;
}

export function findTakenTable(
    tableIds: string[],
    availability: TableAvailabilityDTO[]
): string | undefined {
    return tableIds.find((tableId) =>
        availability.some((table) => table.tableId === tableId && table.isBooked)
    );
}

export function isReservableTable(table: { isActive: boolean; qrCode: string | null }): boolean {
    return table.isActive && !!table.qrCode;
}

export function keepFreeTables(
    tableIds: string[],
    unavailableTables: Record<string, string | null>
): string[] {
    return tableIds.filter((tableId) => !(tableId in unavailableTables));
}

export function getSelectedSeats(
    tableIds: string[],
    tables: Array<{ id: string; seats: number }>
): number {
    return tables
        .filter((table) => tableIds.includes(table.id))
        .reduce((sum, table) => sum + table.seats, 0);
}

export function canConfirmReservation(
    values: Pick<ReservationFormValues, 'guestsCount' | 'tableIds'>,
    selectedSeats: number
): boolean {
    return (
        values.tableIds.length > 0 &&
        values.guestsCount !== null &&
        selectedSeats >= values.guestsCount
    );
}

export function isPastSlot(date: string, time: string, now: ReservationWallClock): boolean {
    if (!date || !time) {
        return false;
    }
    return typeof now === 'string' ? `${date}T${time}:00` < now : parseISO(`${date}T${time}`) < now;
}

function digitsOnly(value: string | null): string {
    return (value ?? '').replace(/\D/g, '');
}

export function findDuplicateReservation(
    contact: { email: string; phoneNumber: string },
    date: string,
    reservations: ReservationDTO[],
    ownReservationId: string | null
): ReservationDTO | null {
    const email = contact.email.trim().toLowerCase();
    const phone = digitsOnly(contact.phoneNumber).slice(-9);
    return (
        reservations.find(
            (reservation) =>
                reservation.id !== ownReservationId &&
                getDatePart(reservation.reservationStartDateTime) === date &&
                ((!!email && (reservation.email ?? '').trim().toLowerCase() === email) ||
                    (!!phone && digitsOnly(reservation.phone).slice(-9) === phone))
        ) ?? null
    );
}

function workingHourToMinutes(value: number): number {
    return Math.floor(value) * 60 + Math.round((value % 1) * 100);
}

export function isOutsideOpeningHours(
    date: string,
    time: string,
    workingHours: Partial<Record<DayKey, HourData[]>> | null | undefined,
    durationMinutes = 0
): boolean {
    if (date && time && durationMinutes > 0 && durationMinutes <= MAX_DURATION_MINUTES) {
        const start = parseISO(`${date}T${time}`);
        for (let minute = 0; minute < durationMinutes; minute++) {
            const slot = new Date(start.getTime() + minute * 60000);
            if (
                isOutsideOpeningHours(
                    format(slot, DATE_FORMAT),
                    format(slot, 'HH:mm'),
                    workingHours
                )
            ) {
                return true;
            }
        }
        return false;
    }
    if (!date || !time) {
        return false;
    }
    const day = parseISO(date);
    const minutes = timeToMinutes(time);
    const isOpenToday = getTodayWorkingHours(workingHours, day).some((range) => {
        if (isFullDayTimeRange(range)) {
            return true;
        }
        const from = workingHourToMinutes(range.from as number);
        const to = workingHourToMinutes(range.to as number);
        return to > from ? minutes >= from && minutes < to : minutes >= from;
    });
    if (isOpenToday) {
        return false;
    }
    const isOpenSinceYesterday = getTodayWorkingHours(workingHours, addDays(day, -1)).some(
        (range) => {
            const from = workingHourToMinutes(range.from as number);
            const to = workingHourToMinutes(range.to as number);
            return !isFullDayTimeRange(range) && to <= from && minutes < to;
        }
    );
    return !isOpenSinceYesterday;
}

export function validateReservationForm(
    values: ReservationFormValues,
    now: ReservationWallClock
): ReservationFormErrors {
    const errors: ReservationFormErrors = {};
    const required = (field: string) =>
        i18n.t('form.errors.required', { name: i18n.t(`reservations.fields.${field}`) });

    if (!values.date) {
        errors.date = required('date');
    } else if (values.channel !== 'WalkIn' && values.date < getWallClockString(now).slice(0, 10)) {
        errors.date = i18n.t('reservations.errors.dateInPast');
    }
    if (!values.time) {
        errors.time = required('time');
    } else if (
        values.channel !== 'WalkIn' &&
        !errors.date &&
        isPastSlot(values.date, values.time, now)
    ) {
        errors.time = i18n.t('reservations.errors.timeInPast');
    }
    if (values.isMoreThanMaxGuests) {
        if (
            values.guestsCount === null ||
            !Number.isInteger(values.guestsCount) ||
            values.guestsCount <= MAX_GUESTS_CHIP
        ) {
            errors.guestsCount = i18n.t('reservations.errors.exactGuests', {
                min: MAX_GUESTS_CHIP,
            });
        } else if (values.guestsCount > MAX_GUESTS) {
            errors.guestsCount = i18n.t('reservations.errors.guestsMax', { max: MAX_GUESTS });
        }
    } else if (values.guestsCount === null) {
        errors.guestsCount = required('guests');
    }
    if (
        values.durationMinutes === null ||
        values.durationMinutes <= 0 ||
        values.durationMinutes % SLOT_MINUTES !== 0
    ) {
        errors.durationMinutes = i18n.t('reservations.errors.durationStep');
    } else if (values.durationMinutes > MAX_DURATION_MINUTES) {
        errors.durationMinutes = i18n.t('reservations.errors.durationMax', {
            max: MAX_DURATION_MINUTES,
        });
    }
    if (!values.firstName.trim() && !values.lastName.trim()) {
        errors.firstName = i18n.t('reservations.errors.nameRequired');
        errors.lastName = errors.firstName;
    }
    const email = values.email.trim();
    if (values.channel !== 'WalkIn' && !values.phoneNumber.trim() && !email) {
        errors.phoneNumber = i18n.t('reservations.errors.contactRequired');
        errors.email = errors.phoneNumber;
    } else if (email && !regexHelper.email.test(email)) {
        errors.email = i18n.t('form.errors.email');
    }
    if (values.channel === 'Phone' && !values.phoneNumber.trim()) {
        errors.phoneNumber = required('phone');
    }
    if (values.channel === 'Email' && !email) {
        errors.email = required('email');
    }
    (Object.keys(RESERVATION_MAX_LENGTH) as Array<keyof typeof RESERVATION_MAX_LENGTH>)
        .filter((field) => field !== 'otherAllergies' || values.hasOtherAllergies)
        .filter((field) => values[field].trim().length > RESERVATION_MAX_LENGTH[field])
        .forEach((field) => {
            errors[field] = i18n.t('form.errors.maxLength', {
                name: i18n.t(MAX_LENGTH_LABELS[field]),
                length: RESERVATION_MAX_LENGTH[field],
            });
        });
    return errors;
}

export function validateNewReservationForm(
    values: ReservationFormValues,
    now: ReservationWallClock
): ReservationFormErrors {
    const errors = validateReservationForm(values, now);
    if (!values.channel) {
        errors.channel = i18n.t('form.errors.required', {
            name: i18n.t('reservations.fields.channel'),
        });
    }
    return errors;
}

const DEFAULT_NEW_RESERVATION_MINUTES = 18 * 60;

export function getNewReservationFormValues(
    settings: Pick<ReservationModule, 'maxReservationDurationMinutes'>,
    now: Date
): ReservationFormValues {
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const minutes =
        nowMinutes < DEFAULT_NEW_RESERVATION_MINUTES
            ? DEFAULT_NEW_RESERVATION_MINUTES
            : (Math.floor(nowMinutes / SLOT_MINUTES) + 1) * SLOT_MINUTES;
    const isTomorrow = minutes >= MINUTES_IN_DAY;
    const durationMinutes = getDefaultDurationMinutes(settings) ?? FALLBACK_DURATION_MINUTES;
    return {
        date: format(isTomorrow ? addDays(now, 1) : now, DATE_FORMAT),
        time: minutesToTime(isTomorrow ? 0 : minutes),
        guestsCount: 4,
        isMoreThanMaxGuests: false,
        durationMinutes,
        isCustomDuration: !DURATION_PRESETS.includes(durationMinutes),
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        allergenIds: [],
        hasOtherAllergies: false,
        otherAllergies: '',
        specialRequests: '',
        internalNote: '',
        tableIds: [],
        channel: 'Phone',
    };
}

export function reservationToFormValues(reservation: ReservationDTO): ReservationFormValues {
    const durationMinutes = reservation.reservationDurationInMinutes;
    return {
        date: getDatePart(reservation.reservationStartDateTime),
        time: getTimePart(reservation.reservationStartDateTime),
        guestsCount: reservation.isGuestsNumberApproximate ? null : reservation.guestsNumber,
        isMoreThanMaxGuests:
            reservation.isGuestsNumberApproximate || reservation.guestsNumber > MAX_GUESTS_CHIP,
        durationMinutes,
        isCustomDuration: !DURATION_PRESETS.includes(durationMinutes),
        firstName: reservation.firstName ?? '',
        lastName: reservation.lastName ?? '',
        phoneNumber: reservation.phone ?? '',
        email: reservation.email ?? '',
        allergenIds: reservation.allergies,
        hasOtherAllergies: !!reservation.otherAllergies,
        otherAllergies: reservation.otherAllergies ?? '',
        specialRequests: reservation.clientNote ?? '',
        internalNote: reservation.internalNote ?? '',
        tableIds: reservation.tables,
        channel: reservation.channel,
    };
}

export function formValuesToModel(values: ReservationFormValues): UpdateReservationModel {
    const email = values.email.trim();
    return {
        reservationStartDateTime: toWireDateTime(values.date, values.time),
        reservationDurationInMinutes: values.durationMinutes ?? 0,
        guestsNumber: values.guestsCount ?? 0,
        isGuestsNumberApproximate: false,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        phone: values.phoneNumber.trim(),
        email: email || null,
        allergies: values.allergenIds,
        otherAllergies: values.hasOtherAllergies ? values.otherAllergies.trim() : '',
        clientNote: values.specialRequests.trim(),
        tables: values.tableIds,
        channel: values.channel,
        internalNote: values.internalNote ?? '',
    };
}

export function getDateFnsLocale(): Locale {
    return i18n.locale === 'de' ? de : enGB;
}

export function formatReservationDate(date: string, pattern: string): string {
    return format(parseISO(date), pattern, { locale: getDateFnsLocale() });
}

export function formatGuestsCount(
    reservation: Pick<ReservationDTO, 'guestsNumber' | 'isGuestsNumberApproximate'>
): string {
    return reservation.isGuestsNumberApproximate
        ? `${reservation.guestsNumber}+`
        : String(reservation.guestsNumber);
}

export function formatFormGuestsCount(
    values: Pick<ReservationFormValues, 'guestsCount' | 'isMoreThanMaxGuests'>,
    maxGuestsOnline: number
): string {
    if (values.guestsCount !== null) {
        return String(values.guestsCount);
    }
    return values.isMoreThanMaxGuests ? `${maxGuestsOnline}+` : '';
}

export function formatGuestsLabel(guests: string): string {
    if (!guests) {
        return '';
    }
    return guests === '1'
        ? i18n.t('reservations.guestsOne')
        : i18n.t('reservations.guestsMany', { guests });
}

export function formatAssignedTables(
    tableIds: string[],
    tables: Array<{ id: string; label: string; areaName: string }>
): string {
    const labelsByArea: Record<string, string[]> = {};
    tables
        .filter((table) => tableIds.includes(table.id))
        .forEach((table) => {
            labelsByArea[table.areaName] = (labelsByArea[table.areaName] ?? []).concat(table.label);
        });
    return Object.keys(labelsByArea)
        .map((areaName) => `${labelsByArea[areaName].join(' + ')} · ${areaName}`)
        .join(', ');
}

export function formatGuestName(guest: {
    firstName: string | null;
    lastName: string | null;
}): string {
    return [guest.lastName, guest.firstName].filter(Boolean).join(', ');
}

export function formatSeats(seats: number): string {
    return seats === 1 ? i18n.t('reservations.seatsOne') : i18n.t('reservations.seats', { seats });
}

export function formatDuration(minutes: number): string {
    return DURATION_PRESETS.includes(minutes)
        ? i18n.t(`reservations.duration.m${minutes}`)
        : i18n.t('reservations.duration.minutes', { minutes });
}
